import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import type { PaymentMethod } from '@/types/residents/payment';

// Use WARNING icon for error state (red circle with exclamation)
const ErrorIcon = RESIDENTS_SVG_COMPONENTS.WARNING;

interface PaymentMethodDisplayProps {
  paymentMethod?: PaymentMethod | null;
  onAddPress?: () => void;
  onChangePress?: () => void;
  showExpiredError?: boolean;
  error?: string;
  showAddNewWhenExpired?: boolean;
}

/**
 * Payment method display section showing selected method with Change link
 * or Add a payment method link if no method selected
 */
export const PaymentMethodDisplay: React.FC<PaymentMethodDisplayProps> = ({
  paymentMethod,
  onAddPress,
  onChangePress,
  showExpiredError = false,
  error,
  showAddNewWhenExpired = true,
}) => {
  // Use shared utility for consistent label formatting
  const methodLabel = useMemo(() => {
    return getPaymentMethodShortLabel(paymentMethod);
  }, [paymentMethod]);

  const isExpired = paymentMethod?.isExpired;

  const handleChangePress = () => {
    console.log('PaymentMethodDisplay: Change button pressed');
    onChangePress?.();
  };

  const handleAddPress = () => {
    console.log('PaymentMethodDisplay: Add button pressed');
    onAddPress?.();
  };

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>Payment method</Text>
      
      <View style={styles.methodContent}>
        {paymentMethod ? (
          // Has payment method
          <>
            {/* Show method with Change link */}
            <View style={styles.methodRow}>
              <View style={styles.methodInfo}>
                <Text style={[
                  styles.methodLabel,
                  isExpired && styles.methodLabelExpired,
                ]}>
                  {methodLabel}
                </Text>
                {isExpired && (
                  <Text style={styles.expiredLabel}>Expired</Text>
                )}
              </View>
              {/* Always show Change button when payment method exists - user can change to different method */}
              <TouchableOpacity onPress={handleChangePress} activeOpacity={0.7}>
                <Text style={styles.changeLink}>Change</Text>
              </TouchableOpacity>
            </View>

            {/* Show "Add a new payment method" link when expired */}
            {isExpired && showAddNewWhenExpired && (
              <TouchableOpacity
                style={styles.addMethodRow}
                onPress={handleAddPress}
                activeOpacity={0.7}
              >
                <Text style={styles.addMethodLink}>Add a new payment method</Text>
              </TouchableOpacity>
            )}
          </>
        ) : (
          // No payment method - show Add link
          <TouchableOpacity
            style={styles.addMethodRow}
            onPress={handleAddPress}
            activeOpacity={0.7}
          >
            <Text style={styles.addMethodLink}>Add a payment method</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Error message for expired payment method or no method selected */}
      {(showExpiredError || error) && (
        <View style={styles.errorRow}>
          {ErrorIcon && (
            <ErrorIcon width={20} height={20} color="#FF3333" />
          )}
          <Text style={styles.errorText}>
            {error || 'Please add a payment method'}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  methodContent: {
    paddingLeft: 16,
  },
  methodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  methodInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  methodLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  methodLabelExpired: {
    color: MARKETPLACE_COLORS.textMuted,
  },
  expiredLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FF3333',
    lineHeight: 18,
  },
  changeLink: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.accentBlue,
    lineHeight: 21,
  },
  addMethodRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addMethodLink: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.accentBlue,
    lineHeight: 21,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  errorText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FF3333',
    lineHeight: 21,
  },
});
